/**
 * =============================================================================
 * Componente: AffiliateCSVUploadStep
 * Versão: 1.0.0
 *
 * Descrição:
 *   Etapa 1 do wizard de importação de afiliados — upload do arquivo CSV,
 *   detecção automática de delimitador, escolha do DDI padrão e preview das
 *   primeiras linhas. Reaproveita visualmente o `CSVUploadStep` da importação
 *   de listas, porém usa parser/template específicos para afiliados.
 * =============================================================================
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  Globe,
} from 'lucide-react';

import {
  parseAffiliateCSV,
  AffiliateCSVParseResult,
  AFFILIATE_REQUIRED_COLUMNS,
  downloadAffiliateCSVTemplate,
} from '@/lib/affiliate-csv-utils';
import { formatFileSize } from '@/lib/csv-utils';
import { countryCodes } from '@/lib/country-codes';

interface Props {
  /**
   * Dispara automaticamente sempre que há um CSV válido parseado (ou o DDI muda).
   * Envia `null` quando o usuário troca/reseta o arquivo, para invalidar o estado no pai.
   * O avanço para a próxima etapa é controlado pelo rodapé fixo do wizard.
   */
  onReady: (
    payload: { data: AffiliateCSVParseResult; defaultCountryCode: string; sourceFile?: File } | null,
  ) => void;
}

export default function AffiliateCSVUploadStep({ onReady }: Props) {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<AffiliateCSVParseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [defaultCountryCode, setDefaultCountryCode] = useState('55');

  // Notifica o wizard sempre que houver CSV válido (ou inválido/removido)
  useEffect(() => {
    if (parseResult) {
      onReady({ data: parseResult, defaultCountryCode, sourceFile: file ?? undefined });
    } else {
      onReady(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseResult, defaultCountryCode, file]);

  const handleFile = useCallback(async (selected: File) => {
    setError(null);
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setError('Selecione um arquivo .csv');
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      setError('Arquivo muito grande (máx. 10MB)');
      return;
    }
    setFile(selected);
    try {
      const text = await selected.text();
      const result = parseAffiliateCSV(text);
      if (result.errors.length > 0) {
        setError(result.errors.join('. '));
        return;
      }
      if (result.totalRows === 0) {
        setError('CSV sem linhas de dados');
        return;
      }
      if (result.totalRows > 5000) {
        setError('Limite de 5.000 afiliados por importação');
        return;
      }
      setParseResult(result);
    } catch {
      setError('Não foi possível ler o arquivo. Verifique se é um CSV válido.');
    }
  }, []);

  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files[0]; if (f) handleFile(f);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (f) handleFile(f);
  };
  const reset = () => { setFile(null); setParseResult(null); setError(null); };

  const selectedCountry = countryCodes.find((c) => c.code.replace('+', '') === defaultCountryCode);

  return (
    <div className="space-y-6 w-full" style={{ maxWidth: '100%', overflow: 'hidden', contain: 'inline-size' }}>
      {/* Cabeçalho do step */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold">Selecione o arquivo CSV</h3>
          <p className="text-sm text-muted-foreground">
            Colunas obrigatórias: <code className="bg-muted px-1 rounded">nome</code>,{' '}
            <code className="bg-muted px-1 rounded">email</code>. Opcionais: telefone, documento, tipo_documento, observacoes.
          </p>
        </div>
        <Button variant="outline" onClick={downloadAffiliateCSVTemplate}>
          <Download className="h-4 w-4 mr-2" />
          Baixar Modelo
        </Button>
      </div>

      {/* Drop zone ou resumo */}
      {!parseResult ? (
        <Card
          className={`border-2 border-dashed transition-colors ${
            isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
          }`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <CardContent className="py-12">
            <div className="text-center">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">Arraste o arquivo CSV aqui</p>
              <p className="text-sm text-muted-foreground mb-4">ou clique para selecionar</p>
              <label>
                <input type="file" accept=".csv" onChange={onChange} className="hidden" />
                <Button variant="secondary" asChild><span>Selecionar arquivo</span></Button>
              </label>
              <p className="text-xs text-muted-foreground mt-4">Máx. 5.000 linhas • até 10MB</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-medium">{file?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {file && formatFileSize(file.size)} • {parseResult.totalRows} linhas
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={reset}>Trocar arquivo</Button>
              </div>
            </CardContent>
          </Card>

          {/* Colunas detectadas */}
          <div className="space-y-2">
            <h4 className="font-medium">Colunas detectadas:</h4>
            <div className="flex flex-wrap items-center gap-2">
              {parseResult.headers.map((h) => (
                <Badge
                  key={h}
                  variant={(AFFILIATE_REQUIRED_COLUMNS as readonly string[]).includes(h) ? 'default' : 'outline'}
                  className="shrink-0"
                >
                  {h}
                  {(AFFILIATE_REQUIRED_COLUMNS as readonly string[]).includes(h) && <CheckCircle2 className="h-3 w-3 ml-1" />}
                </Badge>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <h4 className="font-medium">Preview (10 primeiras linhas):</h4>
            <div className="relative" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10" />
              <div
                className="border rounded-md overflow-auto scrollbar-thin"
                style={{ width: '100%', maxWidth: '100%' }}
              >
                <div style={{ minWidth: `${Math.max(parseResult.headers.length * 150, 400)}px` }}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 whitespace-nowrap">#</TableHead>
                        {parseResult.headers.map((h) => (
                          <TableHead key={h} className="whitespace-nowrap min-w-[120px]">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parseResult.rows.slice(0, 10).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-muted-foreground whitespace-nowrap">{idx + 1}</TableCell>
                          {parseResult.headers.map((h) => (
                            <TableCell key={h} className="max-w-[200px] truncate whitespace-nowrap">
                              {row[h] || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
            {parseResult.totalRows > 10 && (
              <p className="text-sm text-muted-foreground text-center">
                ... e mais {parseResult.totalRows - 10} registros
              </p>
            )}
          </div>

          {/* Botão "Continuar" agora vive no rodapé fixo do wizard (ImportAffiliatesWizard) */}
        </>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* DDI padrão */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-medium text-sm">Código do País (DDI)</h4>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Aplicado aos telefones que não tiverem DDI explícito
            </p>
          </div>
          <Select value={defaultCountryCode} onValueChange={setDefaultCountryCode}>
            <SelectTrigger className="w-[180px]">
              <SelectValue>
                {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.country} (${selectedCountry.code})` : 'Selecione...'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {countryCodes.slice(0, 10).map((c) => (
                <SelectItem key={c.code} value={c.code.replace('+', '')}>
                  {c.flag} {c.country} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
